<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { setUser as setUserSentry } from '@sentry/vue'
import PDivider from 'primevue/divider'
import { useToast } from 'primevue/usetoast'
import { email as emailValidator } from '@vuelidate/validators'
import AuthService from '../services/AuthService'
import { useActiveUserStore } from '../stores/ActiveUserStore'
import { useSentry } from '../composables/sentry'
import { FlowState } from '../constants/flowState'

const activeUserStore = useActiveUserStore()
const email = ref('')
// const email = ref(import.meta.env.VITE_USER || '')
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { handleError } = useSentry()
// TODO: blip to login page while checking sign in status
const loadSignInForm = ref(false) // Does this handle it enough?

const loginRoutingPush = () => {
  if (route.query.redirect)
    router.push(`${route.query.redirect}`)
  else
    router.push('/')
}

const setUser = (i: any) => {
  activeUserStore.setUser({
    userAuthRecord: i.userAuthRecord,
    userDatabaseRecord: {
      uid: i.userAuthRecord.uid,
      ...i.userDatabaseRecord
    },
    permissions: i.permissions,
    customClaims: i.customClaims,
    providers: i.providers
  })

  // Set data to send with Sentry errors
  setUserSentry({
    email: i.userAuthRecord.email,
    Roles: i.customClaims || []
  })
  loginRoutingPush()
}

// Deprecated 2024-05-02 @bmrankin
// To be removed in next version
const signInMicrosoft = async () => {
  try {
    const res: any = await AuthService.signInWithAuthProvider('microsoft')
    if (!res.success)
      throw new Error('There was an issue sigining in')

    setUser(res)

    if (route.query.redirect)
      router.push(`${route.query.redirect}`)
    else
      router.push('/')
  }
  catch (error: any) {
    handleError(error)
  }
}

const requestLinkFlow = ref(FlowState.Idle)
const validEmail = computed(() => {
  if (!email.value?.length)
    return false
  return emailValidator.$validator(email.value, null, null)
})

const disableRequestLink = computed(() => {
  // All states must be true to enable
  const arr: any[] = [
    [FlowState.Idle, FlowState.Error].includes(requestLinkFlow.value),
    validEmail.value
  ]

  return !arr.every((i: boolean) => i === true)
})

async function requestSignInLink() {
  if (disableRequestLink.value)
    return

  requestLinkFlow.value = FlowState.Pending
  const res = await AuthService.requestSignInLink({ email: email.value.trim() })
  if (res.success) {
    requestLinkFlow.value = FlowState.Complete
  }
  else {
    requestLinkFlow.value = FlowState.Error
    toast.add({
      detail: 'Error requesting sign in link. Please try again or reach out to the dev team for assistance.',
      severity: 'error',
      life: 4500,
      summary: 'Request Sign In Link'
    })
  }
}

onMounted(async () => {
  // Not sure if this is the best place to have the router.push logic. However under the current setup all routes flow through Login if there are issue with the user auth
  const res = await AuthService.initAuth()
  if (res.userAuthRecord && res.userAuthRecord.uid && activeUserStore.auth.uid !== res.userAuthRecord.uid)
    setUser(res)
  else if (res.userAuthRecord && res.userAuthRecord.uid === activeUserStore.auth.uid)
    loginRoutingPush()
  // else {
  //   router.push('/') // this could create a unresolved loop?
  // }
  loadSignInForm.value = true
})
</script>

<template>
  <div class="min-h-screen flex align-items-center justify-content-center surface-50">
    <div
      v-if="loadSignInForm"
      class="surface-card p-4 shadow-2 border-round w-full md:w-8 lg:w-4"
    >
      <div class="text-center mb-5">
        <div>
          <img
            alt="Daughtridge Sales logo"
            src="@/assets/Daughtridge-Sales-Color-NoSpacing.svg"
            height="50"
            class="mb-3"
          >
        </div>
        <p class="text-600 font-medium line-height-3">
          This portal is for Daughtridge Sales Employees and authorized users.
        </p>
      </div>
      <template v-if="requestLinkFlow === FlowState.Complete">
        <div class="bg-green-50 p-4 border-round border-1 border-green-300">
          <div>If an authorized account for {{ email }} exists you will receive a sign in link in your email from <span class="font-bold text-primary">DascoSales No Reply</span>. Please allow up to three (3) minutes for it to arrive.</div>
          <div class="mt-4">
            Be sure to check your spam/junk folder. Reach out to the dev team if you do not receive an email.
          </div>
          <div class="mt-4">
            You can close this tab.
          </div>
        </div>
      </template>
      <template v-else>
        <div>
          <label
            for="email"
            class="block text-900 font-medium mb-2"
          >Email</label>
          <p-input-text
            id="email"
            v-model="email"
            type="text"
            class="w-full mb-3"
            @keyup.enter="requestSignInLink"
          />
          <div class="text-center">
            <p-btn
              class="w-20rem mb-2 justify-content-center"
              :disabled="disableRequestLink"
              @click="requestSignInLink"
            >
              <fa-icon
                v-if="requestLinkFlow === FlowState.Pending"
                icon="fas fa-loader"
                class="fa-spin mr-2"
              />
              <fa-icon
                v-else
                icon="fas fa-paper-plane"
                class="mr-2"
              />
              Request Sign In Link
            </p-btn>
            <p-divider
              align="center"
              type="solid"
            >
              <b class="mx-2">OR</b>
            </p-divider>
            <p-btn
              aria-label="Sign in with Microsoft 365 Account"
              label="Microsoft"
              class="my-2 p-button-outlined text-gray-700 w-20rem"
              @click="signInMicrosoft"
            >
              <div class="flex align-items-center justify-content-center mx-auto">
                <img
                  alt="Microsoft logo"
                  class="mr-3"
                  src="../assets/microsoft.svg"
                >
                <span>
                  Sign in with Microsoft
                </span>
              </div>
            </p-btn>
            <div class="font-italic text-xs">
              <sup class="text-red-500">*</sup>
              Login with Microsoft is deprecated and will be removed in future release.
            </div>
          </div>
        </div>
      </template>
    </div>
    <p-toast
      position="top-left"
      style="z-index: 20;"
    />
  </div>
</template>
